/**
 * Playlist Detail Page
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { usePlayerStore } from '@/stores/player';
import { PlayIcon, ShuffleIcon } from '@/components/icons';
import { LoadingGrid } from '@/components/common/LoadingGrid';

export function PlaylistPage() {
  const { id } = useParams<{ id: string }>();
  const [playlist, setPlaylist] = useState<any>(null);
  const [songs, setSongs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { play } = usePlayerStore();

  useEffect(() => {
    async function loadPlaylist() {
      if (!id) return;

      try {
        const response = await api.getPlaylist(id);
        if (response.data) {
          setPlaylist(response.data.playlist);
          setSongs(response.data.songs as any[]);
        }
      } catch (error) {
        console.error('Failed to load playlist:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadPlaylist();
  }, [id]);

  if (isLoading) {
    return <LoadingGrid />;
  }

  if (!playlist) {
    return (
      <div className="text-center py-12">
        <p className="text-on-surface-variant">Playlist not found</p>
      </div>
    );
  }

  const thumbnail = playlist.thumbnail || playlist.thumbnails?.[playlist.thumbnails.length - 1]?.url;

  return (
    <div className="min-h-full">
      {/* Header */}
      <header className="px-4 pt-8 pb-6 flex flex-col sm:flex-row items-center gap-6">
        <img
          src={thumbnail || './pwa-192x192.webp'}
          alt={playlist.title}
          className="w-40 h-40 rounded-xl shadow-2xl object-cover"
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
          onError={(e) => {
            (e.target as HTMLImageElement).src = './pwa-192x192.webp';
          }}
        />
        <div className="text-center sm:text-left flex-1">
          <p className="text-sm text-on-surface-variant uppercase tracking-wider mb-1">
            Playlist
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">
            {playlist.title}
          </h1>
          <p className="text-on-surface-variant mb-4">
            {songs.length} songs
          </p>
          <div className="flex items-center justify-center sm:justify-start gap-3">
            <button
              className="btn-primary flex items-center gap-2"
              onClick={() => songs.length > 0 && play(songs[0], songs)}
            >
              <PlayIcon className="w-5 h-5" />
              Play
            </button>
            <button
              className="btn-secondary flex items-center gap-2"
              onClick={() => {
                const shuffled = [...songs].sort(() => Math.random() - 0.5);
                if (shuffled.length > 0) play(shuffled[0], shuffled);
              }}
            >
              <ShuffleIcon className="w-5 h-5" />
              Shuffle
            </button>
          </div>
        </div>
      </header>

      {/* Track list */}
      <div className="px-4 pb-8">
        <div className="space-y-1">
          {songs.map((song, index) => (
            <button
              key={song.id}
              onClick={() => play(song, songs)}
              className="w-full list-item"
            >
              <span className="w-8 text-center text-on-surface-variant text-sm">
                {index + 1}
              </span>
              <img
                src={song.thumbnail || song.thumbnails?.[0]?.url || './pwa-192x192.webp'}
                alt={song.title}
                className="thumbnail-sm"
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = './pwa-192x192.webp';
                }}
              />
              <div className="flex-1 text-left">
                <p className="font-medium line-clamp-1">{song.title}</p>
                <p className="text-sm text-on-surface-variant line-clamp-1">
                  {song.artists?.map((a: any) => a.name).join(', ')}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
